import { Loader2, MessageSquareText, Phone } from 'lucide-react';

interface SendSmsConfirmModalProps {
  open: boolean;
  /** Patient the confirmation would be sent to. */
  patientName: string;
  /** Patient mobile number, if available. */
  mobile?: string;
  /** Submission is in progress. */
  loading?: boolean;
  /** Whether a usable mobile number is available to send to. */
  smsAvailable?: boolean;
  /** Book the appointment and send the confirmation SMS. */
  onSend: () => void;
  /** Book the appointment without sending any SMS. */
  onSkip: () => void;
  /** Dismiss the dialog without booking. */
  onCancel: () => void;
}

/**
 * Shown when booking an appointment so staff can decide whether the patient
 * should receive the booking confirmation SMS. Both actions still book the
 * appointment — the choice only controls the SMS.
 */
export function SendSmsConfirmModal({
  open,
  patientName,
  mobile,
  loading = false,
  smsAvailable = true,
  onSend,
  onSkip,
  onCancel,
}: SendSmsConfirmModalProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!loading) onCancel();
      }}
    >
      <div
        className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg shrink-0 bg-primary/10">
              <MessageSquareText size={20} className="text-primary" />
            </div>
            <div className="space-y-1 min-w-0">
              <h3 className="text-base font-semibold text-text">Send confirmation SMS?</h3>
              <p className="text-sm text-text-muted">
                The appointment will be booked now. Do you want to send the confirmation SMS to
                {' '}
                <span className="text-text font-medium">{patientName || 'this patient'}</span>?
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-lg bg-background border border-border px-3 py-2">
            <Phone size={14} className="text-text-muted shrink-0" />
            <span className="text-sm text-text truncate">{mobile || 'No mobile number on file'}</span>
          </div>

          {!smsAvailable && (
            <p className="text-xs text-warning">
              This patient has no valid mobile number, so an SMS cannot be delivered.
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-2 px-5 py-4 border-t border-border">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSkip}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-border text-text hover:bg-background transition-colors disabled:opacity-50"
          >
            Don&apos;t send
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={loading || !smsAvailable}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Send SMS
          </button>
        </div>
      </div>
    </div>
  );
}
